import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "wiser_default_workspace";
const OLD_STORAGE_KEY = "wiser_default_workspace_id";

export type StoredWorkspace = { id: string; slug: string };

// Shared listeners so all hook instances stay in sync
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function parseStored(): StoredWorkspace | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.id === "string" && typeof parsed.slug === "string") {
        return parsed;
      }
    }
  } catch {}
  return null;
}

// Migrate from old key on first access
let migrationChecked = false;
function ensureMigration() {
  if (migrationChecked) return;
  migrationChecked = true;
  if (localStorage.getItem(STORAGE_KEY)) return;
  const oldId = localStorage.getItem(OLD_STORAGE_KEY);
  if (oldId) {
    // Store with empty slug — will be backfilled when workspace list is fetched
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: oldId, slug: "" }));
    localStorage.removeItem(OLD_STORAGE_KEY);
  }
}

function getSnapshot(): string | null {
  ensureMigration();
  return localStorage.getItem(STORAGE_KEY);
}

function notify() {
  for (const cb of listeners) cb();
}

/**
 * Persistent default workspace backed by localStorage.
 * Stores both id and slug. All components stay in sync via useSyncExternalStore.
 */
export function useDefaultWorkspace() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const stored = raw ? parseStored() : null;

  const setDefaultWorkspace = useCallback((ws: StoredWorkspace) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ws));
    notify();
  }, []);

  const clearDefaultWorkspace = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    notify();
  }, []);

  return {
    defaultWorkspaceId: stored?.id ?? null,
    defaultWorkspaceSlug: stored?.slug ?? null,
    setDefaultWorkspace,
    clearDefaultWorkspace,
  } as const;
}

/** Synchronous read for use outside React (e.g. redirect components). */
export function getStoredDefaultWorkspace(): StoredWorkspace | null {
  ensureMigration();
  return parseStored();
}
